// ==============================|| CHATBOT SSE HELPERS ||============================== //
//
// Pure, transport-agnostic helpers for the chatbot SSE flow, extracted from the
// Redux slice so they can be unit-tested in isolation and keep the thunk small.
//
// The chat endpoint streams events as text/event-stream:
//   event: <name>\n
//   data:  <single-line JSON>\n
//   \n                       <- blank line terminates an event
// Progress events (`lookup_started` / `lookup_outcome`) precede exactly one
// terminal event (`answer` or `error`).

// priorTurns limits enforced by the backend (see swagger chatRequest schema).
// Turns are counted per message (user and assistant each count as one), so
// keeping the last 2 question/answer exchanges = 4 turns. Kept deliberately
// small so older context doesn't bleed into and skew later queries.
export const MAX_PRIOR_TURNS = 4;
export const MAX_TURN_CHARS = 2000;
export const MAX_TOTAL_CHARS = 8000;
export const MAX_QUESTION_CHARS = 2000;

// Cap the SSE parse buffer so a server that never emits an event boundary can't
// grow it without limit.
export const MAX_SSE_BUFFER = 100000;

// Unique id for messages. Uses the native crypto.randomUUID (available in
// secure contexts / modern browsers) with a lightweight fallback for
// environments that lack it (e.g. some test runners).
export const genId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

// Build the priorTurns array from existing messages, respecting the backend
// limits: alternating roles, <=10 turns, <=2000 chars each, <=8000 total.
export const buildPriorTurns = (messages = []) => {
  const turns = [];
  for (const m of messages) {
    // Only completed exchanges carry usable text; skip failed/streaming ones.
    if (m.role === 'user') {
      turns.push({ role: 'user', content: (m.content || '').slice(0, MAX_TURN_CHARS) });
    } else if (m.role === 'assistant' && m.status === 'done' && m.text) {
      turns.push({ role: 'assistant', content: m.text.slice(0, MAX_TURN_CHARS) });
    }
  }

  // Keep only the most recent turns and ensure the window starts on a 'user'
  // turn so roles alternate cleanly.
  let recent = turns.slice(-MAX_PRIOR_TURNS);
  while (recent.length && recent[0].role !== 'user') {
    recent = recent.slice(1);
  }

  // Enforce the total-character budget, dropping from the oldest end.
  const totalChars = (arr) => arr.reduce((sum, t) => sum + t.content.length, 0);
  while (recent.length && totalChars(recent) > MAX_TOTAL_CHARS) {
    recent = recent.slice(1);
    while (recent.length && recent[0].role !== 'user') {
      recent = recent.slice(1);
    }
  }
  return recent;
};

// Parse a single SSE block (already split on the blank-line boundary) into
// { eventName, data }.
export const parseSseBlock = (block) => {
  let eventName = 'message';
  let data = '';
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      data += line.slice(5).trim();
    }
  }
  return { eventName, data };
};

// Normalize a thrown request error into a { code, errorMessage } shape for the
// UI. The axios interceptor rewrites errors to error.response.data (an object)
// or the string 'Wrong Services', so we accept either. 401 is handled upstream
// by the interceptor (token cleared + redirect) and never reaches here.
export const normalizeStreamError = (error) => ({
  code: error?.code || 'SERVICE_UNAVAILABLE',
  errorMessage: error?.errorMessage || (typeof error === 'string' ? error : error?.message) || 'Request failed.'
});

// Read a ReadableStream (from a fetch/axios stream response) and invoke onEvent
// for each parsed SSE event. onEvent(eventName, payload) returns a truthy value
// to signal a terminal event and stop reading.
//
// Returns the reason the read finished:
//   'terminal' — onEvent asked to stop (answer/error handled by caller)
//   'overflow' — buffer exceeded maxBuffer before any boundary
//   'eof'      — stream closed without a terminal event
//
// Malformed JSON events are skipped rather than aborting the whole stream.
export async function consumeSseStream(reader, onEvent, { maxBuffer = MAX_SSE_BUFFER } = {}) {
  const decoder = new TextDecoder();
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    if (buffer.length > maxBuffer) {
      return 'overflow';
    }

    // SSE events are separated by a blank line.
    let sepIndex;
    while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);
      if (!block.trim()) continue;

      const { eventName, data } = parseSseBlock(block);
      if (!data) continue;

      let payload;
      try {
        payload = JSON.parse(data);
      } catch (e) {
        continue;
      }

      if (onEvent(eventName, payload)) {
        return 'terminal';
      }
    }
  }

  return 'eof';
}
